// Solve→carve generator. Starts from a full solution and blanks pairs of
// rotationally-symmetric cells, keeping the blank iff the remaining grid is
// (1) still uniquely solvable and (2) still solvable within the target
// difficulty tier. Stops when no further symmetric blank survives both
// checks. Determinism: same (seed, difficulty) → same puzzle.

import { hasUniqueSolution } from '../game/UniquenessChecker.js';
import {
  CELL_COUNT,
  cloneGrid,
  type Difficulty,
  type Grid,
  type Puzzle,
} from '../game/types.js';
import { mulberry32, shuffleInPlace } from '../utils/prng.js';
import { generateSolution } from './SolutionGenerator.js';
import { MAX_RANK_FOR_DIFFICULTY, rateGrid } from './Difficulty.js';

/** Bumped when the carver's algorithm or rating changes shape so saved /
 * shared puzzles can be cleanly invalidated. */
export const GENERATOR_VERSION = 1;

const LAST_INDEX = CELL_COUNT - 1;

/**
 * Generate a `(seed, difficulty)`-deterministic puzzle.
 *
 * Walks cells in PRNG-shuffled order. For each cell, blanks it together with
 * its 180°-rotational partner (`80 - i`); keeps the blank iff the puzzle
 * stays unique-solution AND its `rateGrid` ranks at or below the tier's
 * ceiling. Restores both cells on failure and continues.
 */
export function carvePuzzle(seed: number, difficulty: Difficulty): Puzzle {
  const solution = generateSolution(seed);
  const givens = cloneGrid(solution);
  const order = buildVisitOrder(seed);
  const targetRank = MAX_RANK_FOR_DIFFICULTY[difficulty];
  const visited = new Uint8Array(CELL_COUNT);

  for (const i of order) {
    if (visited[i] === 1) continue;
    const j = LAST_INDEX - i;
    visited[i] = 1;
    visited[j] = 1;
    tryBlankPair(givens, i, j, targetRank);
  }

  return { givens, solution, seed, difficulty, generatorVersion: GENERATOR_VERSION };
}

function buildVisitOrder(seed: number): number[] {
  const order = Array.from({ length: CELL_COUNT }, (_, i) => i);
  shuffleInPlace(mulberry32(seed), order);
  return order;
}

// Tentatively blanks the symmetric pair (i, j) in `givens`, restoring both
// cells if either uniqueness or the tier-rank ceiling would be violated.
// When i === j (i.e. the center cell), only one cell is blanked.
function tryBlankPair(givens: Grid, i: number, j: number, targetRank: number): void {
  const savedI = givens[i];
  const savedJ = givens[j];
  if (savedI === undefined || savedJ === undefined) return;
  givens[i] = 0;
  if (i !== j) givens[j] = 0;

  if (!passesTier(givens, targetRank)) {
    givens[i] = savedI;
    if (i !== j) givens[j] = savedJ;
  }
}

// Tier check: cheap uniqueness brute-force first (most rejections come from
// non-uniqueness), then the logical-solver rating only on the survivors.
function passesTier(givens: Grid, targetRank: number): boolean {
  if (!hasUniqueSolution(givens)) return false;
  const tier = rateGrid(givens);
  if (tier === null) return false;
  return MAX_RANK_FOR_DIFFICULTY[tier] <= targetRank;
}
